import {
    EonUiButton,
    EonUiDropdown,
    EonUiDropdownOption,
    EonUiFileUpload,
    EonUiInput,
} from "@eon-ui/eon-ui-components-react";
import { EnvironmentConfig } from "@me8eon/config";
import { useJwtToken } from "@me8eon/react_authentication";
import { FormEventHandler, useState, useEffect, useRef } from "react";
import { OnboardingAppData } from "./OnboardingPortal";
import {
    OnboardingCompanyIds,
    DefaultIconsList,
} from "./OnboardingPortal.constants";
import AppOnboardingPreviewCard from "./AppOnboardingPreviewCard";
import {
    generateAppIdentifiers,
    getCompanyPayloadList,
    getAppCompanySelectionForForm,
    getCompanyNameFromId,
    encodeCompanyOptionValue,
    isOtherSelected,
} from "./OnboardingPortal.utils";
import OnboardingFormLabel from "./components/OnboardingFormLabel";
import { useTranslation } from "@me8eon/translation";
import Modal from "me-at-eon/src/components/modal/Modal";
import useOnboardingPortalStore from "./OnboardingPortal.Store";

const readFileAsDataUrl = async (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () =>
            resolve(typeof reader.result === "string" ? reader.result : "");
        reader.onerror = () =>
            reject(reader.error ?? new Error("Failed to read icon file"));
        reader.readAsDataURL(file);
    });
};

const AppOnboardingForm = ({
    appData,
    onSubmit,
    onCancel,
}: {
    appData?: OnboardingAppData | null;
    onSubmit: () => void;
    onCancel?: () => void;
}) => {
    const fileUploadContainerRef = useRef<HTMLDivElement | null>(null);
    const jwt = useJwtToken();
    const t = useTranslation();
    const { setShowModule } = useOnboardingPortalStore();
    const [appTitle, setAppTitle] = useState<string>("");
    const [appSubTitle, setAppSubTitle] = useState<string>("");
    const [appUrl, setAppUrl] = useState<string>("");
    const [keywords, setKeywords] = useState<string>("");
    const [companiesAllowed, setCompaniesAllowed] = useState<string>("");
    const [companiesRestricted, setCompaniesRestricted] = useState<string>("");
    const [companiesAllowedOther, setCompaniesAllowedOther] =
        useState<string>("");
    const [companiesRestrictedOther, setCompaniesRestrictedOther] =
        useState<string>("");
    const [appTooltip, setAppTooltip] = useState<string>("");
    const [titleInvalid, setTitleInvalid] = useState(false);
    const [subTitleInvalid, setSubTitleInvalid] = useState(false);
    const [keywordsInvalid, setKeywordsInvalid] = useState(false);
    const [tooltipInvalid, setTooltipInvalid] = useState(false);
    const [urlInvalid, setUrlInvalid] = useState(false);
    const [showIconModal, setShowIconModal] = useState(false);
    const [icon, setIcon] = useState<string>("");
    const [iconDataUrl, setIconDataUrl] = useState<string>("");
    const [iconPreviewUrl, setIconPreviewUrl] = useState<string>("");
    const toggleIconModal = () => setShowIconModal((prev) => !prev);

    useEffect(() => {
        if (appData?.id) {
            setAppTitle(appData.title || "");
            setAppSubTitle(appData.subTitle || "");
            setAppUrl(appData.url || "");
            setKeywords(appData.keywords.join(", ") || "");
            const allowedSelection = getAppCompanySelectionForForm(
                appData.companiesAllowed
            );
            const restrictedSelection = getAppCompanySelectionForForm(
                appData.companiesRestricted
            );
            setCompaniesAllowed(allowedSelection.selectedCompanies);
            setCompaniesRestricted(restrictedSelection.selectedCompanies);
            setCompaniesAllowedOther(allowedSelection.otherCompanies);
            setCompaniesRestrictedOther(restrictedSelection.otherCompanies);
            setAppTooltip(appData.tooltip || "");
            setIconDataUrl("");
            setIconPreviewUrl(appData.iconDataUrl || "");
            setIcon(appData.icon || "");
        }
    }, [appData]);

    useEffect(() => {
        const host = fileUploadContainerRef.current?.querySelector(
            "eon-ui-file-upload"
        ) as HTMLElement | null;

        if (!host?.shadowRoot) {
            return;
        }

        const hideButtonWrapper = async () => {
            const buttonWrapper = host.shadowRoot?.querySelector(
                ".eonui-fileupload__button-wrapper"
            ) as HTMLElement | null;
            if (buttonWrapper) {
                buttonWrapper.style.display = "none";
            }
            const fileSelectorEl = host.shadowRoot?.querySelector(
                ".eonui-fileupload__dropzone"
            ) as HTMLElement | null;
            if (fileSelectorEl) {
                const lastChild =
                    fileSelectorEl.lastElementChild as HTMLElement | null;
                if (lastChild) {
                    let browseTextEl = null;
                    while (browseTextEl === null) {
                        browseTextEl = lastChild?.querySelector(
                            ".hydrated"
                        ) as HTMLElement | null;
                        if (browseTextEl) {
                            browseTextEl.style.marginLeft = "4px";
                        }
                        await new Promise((resolve) =>
                            setTimeout(resolve, 100)
                        );
                    }
                }
            }
        };

        const bindFileInput = () => {
            const fileInput = host.shadowRoot?.querySelector(
                'input[type="file"]'
            ) as HTMLInputElement | null;

            if (!fileInput) {
                return () => undefined;
            }

            const handleFileChange = async () => {
                const selectedFile = fileInput.files?.[0];
                if (!selectedFile) {
                    setIconDataUrl("");
                    setIconPreviewUrl(appData?.iconDataUrl || "");
                    return;
                }

                try {
                    const nextIconDataUrl =
                        await readFileAsDataUrl(selectedFile);
                    setIconDataUrl(nextIconDataUrl);
                    setIconPreviewUrl(nextIconDataUrl);
                } catch (error) {
                    console.error("Failed to process icon file", error);
                }
            };

            fileInput.addEventListener("change", handleFileChange);

            return () => {
                fileInput.removeEventListener("change", handleFileChange);
            };
        };

        hideButtonWrapper();
        let removeFileInputListener = bindFileInput();

        const observer = new MutationObserver(() => {
            hideButtonWrapper();
            removeFileInputListener();
            removeFileInputListener = bindFileInput();
        });

        observer.observe(host.shadowRoot, {
            childList: true,
            subtree: true,
        });

        return () => {
            observer.disconnect();
            removeFileInputListener();
        };
    }, [appData?.iconDataUrl]);

    useEffect(() => {
        const textareaInputNames = ["app-keywords", "tooltip"];
        textareaInputNames.forEach((name) => {
            const el = document.querySelector<HTMLElement>(
                `eon-ui-input[name="${name}"]`
            );
            if (el?.shadowRoot) {
                const existingStyle = el.shadowRoot.querySelector(
                    "style[data-placeholder-wrap-fix]"
                );
                if (!existingStyle) {
                    const style = document.createElement("style");
                    style.setAttribute("data-placeholder-wrap-fix", "true");
                    style.textContent =
                        "::placeholder { white-space: normal !important; overflow: visible !important; text-overflow: clip !important; }";
                    el.shadowRoot.appendChild(style);
                }
            }
        });
    }, []);

    const resetForm = () => {
        setAppTitle("");
        setAppSubTitle("");
        setAppUrl("");
        setKeywords("");
        setCompaniesAllowed("");
        setCompaniesRestricted("");
        setCompaniesAllowedOther("");
        setCompaniesRestrictedOther("");
        setAppTooltip("");
        setTitleInvalid(false);
        setSubTitleInvalid(false);
        setKeywordsInvalid(false);
        setTooltipInvalid(false);
        setUrlInvalid(false);
        setIconDataUrl("");
        setIconPreviewUrl("");
        setIcon("");
    };

    const validateForm = (): boolean => {
        const trimmedTitle = appTitle.trim();
        const trimmedSubTitle = appSubTitle.trim();
        const trimmedUrl = appUrl.trim();
        const trimmedKeywords = keywords.trim();
        const trimmedTooltip = appTooltip.trim();

        const isTitleValid = !!trimmedTitle;
        const isSubTitleValid = !!trimmedSubTitle;
        const isUrlValid = !!trimmedUrl;
        const isKeywordsValid = !!trimmedKeywords;
        const isTooltipValid = !!trimmedTooltip;

        setTitleInvalid(!isTitleValid);
        setSubTitleInvalid(!isSubTitleValid);
        setUrlInvalid(!isUrlValid);
        setKeywordsInvalid(!isKeywordsValid);
        setTooltipInvalid(!isTooltipValid);

        return (
            isTitleValid &&
            isSubTitleValid &&
            isUrlValid &&
            isKeywordsValid &&
            isTooltipValid
        );
    };

    const handleSubmit: FormEventHandler = async (event) => {
        event.preventDefault();
        if (!validateForm()) {
            return;
        }

        const { appId, appAlias } = generateAppIdentifiers(appTitle.trim());

        const tempAppData: {
            id?: string;
            appId: string;
            alias: string;
            title: string;
            subTitle: string;
            url: string;
            keywords: string[];
            companiesAllowed: string[];
            companiesRestricted: string[];
            tooltip: string;
            iconDataUrl: string;
            icon?: string;
            iconBlobName?: string;
            iconMimeType?: string;
        } = {
            appId: appData?.appId ? appData.appId : appId,
            alias: appData?.alias ? appData.alias : appAlias,
            title: appTitle.trim(),
            subTitle: appSubTitle.trim(),
            url: appUrl.trim(),
            keywords: keywords
                .split(",")
                .map((kw) => kw.trim())
                .filter((kw) => kw.length > 0),
            companiesAllowed: getCompanyPayloadList(
                companiesAllowed,
                companiesAllowedOther ?? ""
            ),
            companiesRestricted: getCompanyPayloadList(
                companiesRestricted,
                companiesRestrictedOther ?? ""
            ),
            tooltip: appTooltip.trim(),
            iconDataUrl,
            icon,
        };

        if (appData?.id) {
            tempAppData.id = appData.id;
            if (!iconDataUrl) {
                tempAppData.iconBlobName = appData.iconBlobName as
                    | string
                    | undefined;
                tempAppData.iconMimeType = appData.iconMimeType as
                    | string
                    | undefined;
            }
        }

        const API_BASE_URL = EnvironmentConfig.nodeService;
        const API_ENDPOINT = `${API_BASE_URL}/service/onboarding/apps`;

        try {
            const token = await jwt();
            const response = await fetch(API_ENDPOINT, {
                method: appData?.id ? "PUT" : "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                    app: tempAppData,
                }),
            });
            if (!response.ok) {
                const errorBody = await response.json();
                throw new Error(
                    `Failed to set app data: ${response.status} ${response.statusText}${errorBody?.error ? ` - ${errorBody.error}` : ""}`
                );
            }
            const data: { isSuccess: boolean } = await response.json();
            if (data.isSuccess !== true) {
                console.warn(
                    "API call succeeded, but response not marked as success."
                );
            }
            alert(t("onboardingPortal.appOnboardingForm.successAlert"));
            onSubmit();
        } catch (error) {
            console.error("Error in handleSubmit:", error);
            throw error;
        }
        if (appData?.id && onCancel) {
            onCancel();
        }

        resetForm();
    };

    return (
        <div>
            <div style={{ display: "flex", flexDirection: "row", gap: "16px" }}>
                <form className="onboarding-portal--form">
                    <div className="onboarding-portal--form--row">
                        <div className="onboarding-portal--form--field">
                            <OnboardingFormLabel
                                label={t(
                                    "onboardingPortal.appOnboardingForm.appTitleLabel"
                                )}
                                tooltip={t(
                                    "onboardingPortal.appOnboardingForm.appTitleTooltip"
                                )}
                            />
                            <EonUiInput
                                type="text"
                                name="app-title"
                                value={appTitle}
                                placeholder={t(
                                    "onboardingPortal.appOnboardingForm.appTitlePlaceholder"
                                )}
                                required
                                invalid={titleInvalid}
                                errorMessage={t(
                                    "onboardingPortal.appOnboardingForm.appTitleError"
                                )}
                                onValueChanged={(e) => {
                                    setAppTitle(e.target.value);
                                    if (titleInvalid && e.target.value.trim()) {
                                        setTitleInvalid(false);
                                    }
                                }}
                            />
                        </div>
                    </div>
                    <div className="onboarding-portal--form--row-grid">
                        <div className="onboarding-portal--form--field">
                            <OnboardingFormLabel
                                label={t(
                                    "onboardingPortal.appOnboardingForm.appSubTitleLabel"
                                )}
                                tooltip={t(
                                    "onboardingPortal.appOnboardingForm.appSubTitleTooltip"
                                )}
                            />
                            <EonUiInput
                                type="text"
                                name="app-subtitle"
                                value={appSubTitle}
                                placeholder={t(
                                    "onboardingPortal.appOnboardingForm.appSubTitlePlaceholder"
                                )}
                                required
                                invalid={subTitleInvalid}
                                errorMessage={t(
                                    "onboardingPortal.appOnboardingForm.appSubTitleError"
                                )}
                                onValueChanged={(e) => {
                                    setAppSubTitle(e.target.value);
                                    if (
                                        subTitleInvalid &&
                                        e.target.value.trim()
                                    ) {
                                        setSubTitleInvalid(false);
                                    }
                                }}
                            />
                        </div>
                        <div className="onboarding-portal--form--field">
                            <OnboardingFormLabel
                                label={t(
                                    "onboardingPortal.appOnboardingForm.appUrlLabel"
                                )}
                                tooltip={t(
                                    "onboardingPortal.appOnboardingForm.appUrlTooltip"
                                )}
                            />
                            <EonUiInput
                                type="text"
                                name="app-url"
                                value={appUrl}
                                placeholder={t(
                                    "onboardingPortal.appOnboardingForm.appUrlPlaceholder"
                                )}
                                required
                                invalid={urlInvalid}
                                errorMessage={t(
                                    "onboardingPortal.appOnboardingForm.appUrlError"
                                )}
                                onValueChanged={(e) => {
                                    setAppUrl(e.target.value);
                                    if (urlInvalid && e.target.value.trim()) {
                                        setUrlInvalid(false);
                                    }
                                }}
                            />
                        </div>
                    </div>
                    <div className="onboarding-portal--form--row">
                        <div className="onboarding-portal--form--field">
                            <OnboardingFormLabel
                                label={t(
                                    "onboardingPortal.appOnboardingForm.appCompaniesAllowedLabel"
                                )}
                                tooltip={t(
                                    "onboardingPortal.appOnboardingForm.appCompaniesAllowedTooltip"
                                )}
                            />
                            <EonUiDropdown
                                info
                                infoMessage={t(
                                    "onboardingPortal.appOnboardingForm.appCompaniesAllowedInfoMessage"
                                )}
                                value={companiesAllowed}
                                multiselect
                                showSelectedItems
                                onValueChanged={(e) => {
                                    setCompaniesAllowed(e.target.value);
                                }}
                                height={"200px"}
                                key={companiesAllowed}
                            >
                                {OnboardingCompanyIds.map((companyId) => (
                                    <EonUiDropdownOption
                                        key={companyId}
                                        value={encodeCompanyOptionValue(
                                            companyId
                                        )}
                                        label={getCompanyNameFromId(
                                            companyId
                                        )}
                                    />
                                ))}
                                <EonUiDropdownOption
                                    key="Other"
                                    value="Other"
                                    label="Other"
                                />
                            </EonUiDropdown>
                            {isOtherSelected(companiesAllowed) && (
                                <EonUiInput
                                    type="text"
                                    value={companiesAllowedOther}
                                    label={t(
                                        "onboardingPortal.appOnboardingForm.appCompaniesAllowedOtherLabel"
                                    )}
                                    placeholder={t(
                                        "onboardingPortal.appOnboardingForm.appCompaniesAllowedOtherPlaceholder"
                                    )}
                                    labelOutside
                                    autocomplete={false}
                                    onValueChanged={(e) => {
                                        setCompaniesAllowedOther(
                                            e.target.value
                                        );
                                    }}
                                />
                            )}
                        </div>
                        {/* <div className="onboarding-portal--form--field">
                            <OnboardingFormLabel
                                label={t(
                                    "onboardingPortal.appOnboardingForm.appCompaniesRestrictedLabel"
                                )}
                                tooltip={t(
                                    "onboardingPortal.appOnboardingForm.appCompaniesRestrictedTooltip"
                                )}
                            />
                            <EonUiDropdown
                                info
                                infoMessage={t(
                                    "onboardingPortal.appOnboardingForm.appCompaniesRestrictedInfoMessage"
                                )}
                                value={companiesRestricted}
                                multiselect
                                showSelectedItems
                                onValueChanged={(e) => {
                                    setCompaniesRestricted(e.target.value);
                                }}
                                height={"200px"}
                                key={companiesRestricted}
                            >
                                {OnboardingCompanyIds.map((companyId) => (
                                    <EonUiDropdownOption
                                        key={companyId}
                                        value={encodeCompanyOptionValue(
                                            companyId
                                        )}
                                        label={getCompanyNameFromId(
                                            companyId
                                        )}
                                    />
                                ))}
                                <EonUiDropdownOption
                                    value="Other"
                                    label="Other"
                                />
                            </EonUiDropdown>
                            {isOtherSelected(companiesRestricted) && (
                                <EonUiInput
                                    type="text"
                                    value={companiesRestrictedOther}
                                    label={t(
                                        "onboardingPortal.appOnboardingForm.appCompaniesRestrictedOtherLabel"
                                    )}
                                    placeholder={t(
                                        "onboardingPortal.appOnboardingForm.appCompaniesRestrictedOtherPlaceholder"
                                    )}
                                    labelOutside
                                    autocomplete={false}
                                    onValueChanged={(e) => {
                                        setCompaniesRestrictedOther(
                                            e.target.value
                                        );
                                    }}
                                />
                            )}
                        </div> */}
                    </div>
                    <div className="onboarding-portal--form--row-grid">
                        <div className="onboarding-portal--form--field">
                            <OnboardingFormLabel
                                label={t(
                                    "onboardingPortal.appOnboardingForm.appKeywordsLabel"
                                )}
                                tooltip={t(
                                    "onboardingPortal.appOnboardingForm.appKeywordsTooltip"
                                )}
                            />
                            <EonUiInput
                                textarea
                                rows={2}
                                type="text"
                                value={keywords}
                                name="app-keywords"
                                onValueChanged={(e) => {
                                    setKeywords(e.target.value);
                                    if (
                                        keywordsInvalid &&
                                        e.target.value.trim()
                                    ) {
                                        setKeywordsInvalid(false);
                                    }
                                }}
                                invalid={keywordsInvalid}
                                errorMessage={t(
                                    "onboardingPortal.appOnboardingForm.appKeywordsError"
                                )}
                                placeholder={t(
                                    "onboardingPortal.appOnboardingForm.appKeywordsPlaceholder"
                                )}
                            />
                        </div>
                        <div className="onboarding-portal--form--field">
                            <OnboardingFormLabel
                                label={t(
                                    "onboardingPortal.appOnboardingForm.appTooltipLabel"
                                )}
                                tooltip={t(
                                    "onboardingPortal.appOnboardingForm.appTooltipTooltip"
                                )}
                            />
                            <EonUiInput
                                textarea
                                rows={2}
                                type="text"
                                value={appTooltip}
                                name="tooltip"
                                placeholder={t(
                                    "onboardingPortal.appOnboardingForm.appTooltipPlaceholder"
                                )}
                                onValueChanged={(e) => {
                                    setAppTooltip(e.target.value);
                                    if (
                                        tooltipInvalid &&
                                        e.target.value.trim()
                                    ) {
                                        setTooltipInvalid(false);
                                    }
                                }}
                                invalid={tooltipInvalid}
                                errorMessage={t(
                                    "onboardingPortal.appOnboardingForm.appTooltipError"
                                )}
                            />
                        </div>
                    </div>
                    <div
                        className="onboarding-portal--form--file-upload"
                        ref={fileUploadContainerRef}
                    >
                        <Modal
                            toggleModal={showIconModal}
                            closeModal={toggleIconModal}
                            isCloseIcon={true}
                            closeOnBackdrop={true}
                            modalClass="activity-log-modal"
                        >
                            <div
                                style={{
                                    display: "flex",
                                    flexWrap: "wrap",
                                    gap: 6,
                                    flexDirection: "row",
                                }}
                            >
                                {DefaultIconsList.map((iconName) => (
                                    <EonUiButton
                                        key={iconName}
                                        icon={iconName}
                                        rank={
                                            icon === iconName
                                                ? "primary"
                                                : "functional"
                                        }
                                        onClick={() => {
                                            if (icon === iconName) {
                                                setIcon("");
                                            } else {
                                                setIcon(iconName);
                                            }
                                        }}
                                        // size="small"
                                    />
                                ))}
                            </div>
                            <div
                                style={{
                                    marginTop: 12,
                                    display: "flex",
                                    justifyContent: "flex-end",
                                }}
                            >
                                <EonUiButton
                                    text={t(
                                        "onboardingPortal.appOnboardingForm.closeBtn"
                                    )}
                                    onClick={toggleIconModal}
                                    rank="secondary"
                                    size="small"
                                />
                            </div>
                        </Modal>
                        <OnboardingFormLabel
                            label={t(
                                "onboardingPortal.appOnboardingForm.appIconLabel"
                            )}
                            tooltip={t(
                                "onboardingPortal.appOnboardingForm.appIconTooltip"
                            )}
                        />
                        <div
                            style={{
                                display: "flex",
                                flexDirection: "row",
                                gap: 8,
                                alignItems: "center",
                            }}
                        >
                            <EonUiButton
                                text="Icon library"
                                onClick={toggleIconModal}
                                style={{ flexShrink: 0 }}
                                rank="secondary"
                                icon="arrow_right"
                                iconPosition="right"
                            />
                            <div
                                style={{
                                    color: "#9CA3AF",
                                    padding: "4px 12px",
                                }}
                            >
                                OR
                            </div>
                            <EonUiFileUpload
                                required={false}
                                accept={"image/svg+xml"}
                                multipleFiles={false}
                                showUploadButton={false}
                            />
                        </div>
                    </div>
                    <div
                        style={{
                            display: "flex",
                            flexDirection: "row",
                            gap: "6px",
                        }}
                    >
                        <EonUiButton
                            text={
                                appData?.id
                                    ? t(
                                          "onboardingPortal.appOnboardingForm.updateAppBtn"
                                      )
                                    : t(
                                          "onboardingPortal.appOnboardingForm.submitAppBtn"
                                      )
                            }
                            rank="primary"
                            size="small"
                            onClick={handleSubmit}
                        />
                        <EonUiButton
                            text={t(
                                "onboardingPortal.appOnboardingForm.cancelBtn"
                            )}
                            rank="secondary"
                            size="small"
                            onClick={() => {
                                if (onCancel) {
                                    onCancel();
                                } else {
                                    setShowModule("home");
                                }
                            }}
                        />
                    </div>
                </form>
                <div
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "12px",
                        flexGrow: 1,
                        maxWidth: "25%",
                        minWidth: "200px",
                    }}
                >
                    <div
                        style={{
                            border: "1px solid #DBDADE",
                            backgroundColor: "#FFFFFF",
                            display: "flex",
                            flexDirection: "column",
                            gap: "6px",
                            padding: "16px",
                            borderRadius: "8px",
                        }}
                    >
                        <div
                            style={{
                                fontSize: "18px",
                                fontWeight: 500,
                                color: "#030712",
                                borderBottom: "1px solid #E0E0E0",
                                paddingBottom: "8px",
                            }}
                        >
                            Preview App
                        </div>
                        <div className="onboarding-portal--form--preview">
                            <AppOnboardingPreviewCard
                                data={{
                                    title: appTitle,
                                    subTitle: appSubTitle,
                                    iconUrl: iconPreviewUrl,
                                    icon: icon,
                                }}
                            />
                        </div>
                    </div>
                </div>
            </div>
            <p>{t("onboardingPortal.appOnboardingForm.message")}</p>
        </div>
    );
};

export default AppOnboardingForm;
